import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { orderActions } from "../actions";
import styled from "@emotion/styled";
import OrderTotals from "./OrderTotals";
import Cart from "../pages/Cart";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from '@mui/icons-material/Print';

const styles = {
  dialog: {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgb(0,0,0,0.3)",
    },
    "& .MuiDialog-paper": {
      maxHeight: { xs: "100%", sm: "min(90vh, 100%)" },
      display: "flex",
      flexDirection: "column",
    },
  },
  title: { backgroundColor: "black", color: "white" },
  content: {
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    paddingTop: 1,
  },
  contentScrollbar: {
    "&::-webkit-scrollbar": {
      width: 4,
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "grey",
      width: 4,
    },
  },
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "grey",
    color: theme.palette.common.white,
    fontSize: 19,
  },
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: "#f5f5f5",
    fontSize: 17,
  },
}));

const TableItems = () => {
  const { items } = useSelector((state) => state.order);

  function sumValues(objeto) {
    let suma = 0;

    for (const clave in objeto) {
      if (
        typeof objeto[clave] === "number" &&
        clave !== "productId" &&
        clave !== "webOrderId"
      ) {
        if (clave === "bonifGralBool" && !objeto[clave]) {
          continue;
        }
        suma += objeto[clave];
      }
    }
    return suma.toFixed(2);
  }

  const calculateValue = (unitPrice, descuento) => {
    return sumValues(descuento) === 0
      ? unitPrice
      : (unitPrice * (1 - sumValues(descuento) / 100)).toFixed(2);
  };


  return (
    items && (
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          maxHeight: { xs: "min(55vh, 420px)", sm: "min(60vh, 520px)" },
          overflow: "auto",
          ...styles.contentScrollbar,
        }}
      >
        <Table size="medium" stickyHeader sx={{ width: "100%", minWidth: "min(100%, 720px)" }}>
          <TableHead>
            <TableRow>
              <StyledTableCell align="center" padding="normal" />
              <StyledTableCell align="center" padding="normal">
                Item{" "}
              </StyledTableCell>
              <StyledTableCell align="center" padding="normal" sx={{ minWidth: "10rem" }}>
                Descripción
              </StyledTableCell>
              <StyledTableCell align="center" padding="normal" sx={{ minWidth: "4.5rem" }}>
                Cantidad
              </StyledTableCell>
              <StyledTableCell align="center" padding="normal" sx={{ minWidth: "5.5rem" }}>
                Precio Lista
              </StyledTableCell>
              <StyledTableCell align="center" padding="normal" sx={{ minWidth: "5.5rem" }}>
                Su precio
              </StyledTableCell>
              <StyledTableCell align="center" padding="normal" sx={{ minWidth: "5.5rem" }}>
                SubTotal
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((prod, i) => (
              <TableRow key={`${prod.productId}-${i}`}>
                <StyledTableCell align="center">{i + 1}</StyledTableCell>
                <StyledTableCell align="center">{prod.productId}</StyledTableCell>
                <StyledTableCell
                  align="center"
                  sx={{
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {prod.productDescription}
                </StyledTableCell>
                <StyledTableCell align="center">{prod.quantity}</StyledTableCell>
                <StyledTableCell align="center">{prod.unitPrice}</StyledTableCell>
                <StyledTableCell align="center">
                  {calculateValue(prod.unitPrice, prod.discount)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {(
                    calculateValue(prod.unitPrice, prod.discount) *
                    prod.quantity
                  ).toLocaleString()}
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  );
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PopUpOrder = ({ row, setter }) => {
  const { integrationId, webOrderId } = useSelector((state) => state.order);
  const { dynamicsId } = useSelector((state) => state.account);
  const dispatch = useDispatch();
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down("md"))

  const handleClose = () => {
    !row.checkoutPop && dispatch(orderActions.clear());
    setter({ item: {}, open: false });
  };

  return (
    <Dialog
      open={row.open}
      onClose={() => handleClose()}
      TransitionComponent={Transition}
      keepMounted
      maxWidth={row.checkoutPop ? "lg" : "xl"}
      fullWidth
      sx={styles.dialog}
    >
      {row.checkoutPop ? (
        <DialogTitle sx={styles.title} align="center">
          Pre-visualizacion de pedido
        </DialogTitle>
      ) : (
        <DialogTitle sx={styles.title} align="center">
          Esta viendo la orden numero
          {` ${integrationId ? integrationId : webOrderId}`}
        </DialogTitle>
      )}
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
          "@media print": {
            display: "none"
          },
        }}
      >
        <CloseIcon />
      </IconButton>
      <Divider />
      <DialogContent
        sx={{
          ...styles.content,
          ...styles.contentScrollbar,
          overflow: row.checkoutPop ? "auto" : "hidden",
        }}
      >
        {!row.checkoutPop ? (
          <Grid
            container
            spacing={2}
            sx={{ flex: 1, minHeight: 0, minWidth: 0, alignItems: "flex-start" }}
          >
            <Grid
              item
              xs={12}
              md={9}
              sx={{
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  maxWidth: "100%",
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <TableItems />
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                minWidth: 0,
                maxWidth: { xs: "100%", md: "100%" },
                position: { md: "sticky" },
                top: { md: 0 },
                alignSelf: { md: "flex-start" },
              }}
            >
              {row.open && row.item && Object.keys(row.item).length > 0 && (
                <OrderTotals
                  webOrderId={webOrderId}
                  dynamicsId={dynamicsId}
                  item={row.item}
                  order={true}
                />
              )}
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%" }}>
            <Cart isCheckout={row.checkoutPop} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!row.checkoutPop && (
          <Button
            sx={{
              backgroundColor: "black",
              color: "white",
              transition: "all 0.2s ease-in-out",
              '&:hover': {
                backgroundColor: "black",
                opacity: "0.8"
              },
            }}
            autoFocus
            color="success"
            variant="contained"
            onClick={() => handleClose()}
          >
            Volver
          </Button>
        )}
        {row.checkoutPop && <Button onClick={() => window.print()} size="small" startIcon={<PrintIcon />} sx={{
          width: mobile ? "100%" : "20%",
          backgroundColor: "black",
          color: "white",
          transition: "all 0.2s ease-in-out",
          '&:hover': {
            backgroundColor: "black",
            opacity: "0.8"
          },
          "@media print": {
            display: "none",
          },
        }}>
          Imprimir Pedido
        </Button>}
      </DialogActions>
    </Dialog>
  );
};

export default PopUpOrder;
